/** Deterministic mathematical helpers for lock-step simulation. */

/** Pi constant. */
export const PI = 3.141592653589793;

/** Two Pi constant. */
export const TAU = 6.283185307179586;

/** Half Pi constant. */
export const HALF_PI = 1.5707963267948966;

/**
 * Wrap an angle into the range (-PI, PI].
 * @param a - Input angle in radians.
 * @returns Wrapped angle in (-PI, PI].
 */
export function wrapAngle(a: number): number {
  let w = a - TAU * Math.floor((a + PI) / TAU);
  if (w <= -PI) {
    w += TAU;
  }
  if (w > PI) {
    w -= TAU;
  }
  return w;
}

/**
 * Compute the difference between two angles, wrapped into (-PI, PI].
 * @param from - Starting angle in radians.
 * @param to - Ending angle in radians.
 * @returns Wrapped difference (to - from).
 */
export function angleDiff(from: number, to: number): number {
  return wrapAngle(to - from);
}

/**
 * Compute the length of a 2D vector (x, z).
 * @param x - X component.
 * @param z - Z component.
 * @returns Euclidean length.
 */
export function length(x: number, z: number): number {
  return Math.sqrt(x * x + z * z);
}

/**
 * Clamp a value to a range.
 * @param v - Value to clamp.
 * @param lo - Lower bound.
 * @param hi - Upper bound.
 * @returns Clamped value.
 */
export function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Linear interpolation between two values.
 * @param a - Start value.
 * @param b - End value.
 * @param t - Interpolation parameter (typically 0 to 1).
 * @returns Interpolated value.
 */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Deterministic sine function.
 * @param x - Angle in radians.
 * @returns Sine of x.
 */
export function sin(x: number): number {
  if (x === 0) {
    return 0;
  }
  let r = wrapAngle(x);
  if (r > HALF_PI) {
    r = PI - r;
  } else if (r < -HALF_PI) {
    r = -PI - r;
  }
  const r2 = r * r;
  return r * (1 + r2 * (-1 / 6 + r2 * (1 / 120 + r2 * (-1 / 5040 + r2 * (1 / 362880 + r2 * (-1 / 39916800))))));
}

/**
 * Deterministic cosine function.
 * @param x - Angle in radians.
 * @returns Cosine of x.
 */
export function cos(x: number): number {
  return sin(x + HALF_PI);
}

/**
 * Deterministic atan2 function.
 * @param y - Y component.
 * @param x - X component.
 * @returns Angle in radians.
 */
export function atan2(y: number, x: number): number {
  if (y === 0 && x === 0) {
    return 0;
  }
  if (y === 0 && x > 0) {
    return 0;
  }
  const ax = Math.abs(x);
  const ay = Math.abs(y);
  const lo = Math.min(ax, ay);
  const hi = Math.max(ax, ay);
  const t = lo / hi;
  const t2 = t * t;
  let angle = t * (0.9998660 + t2 * (-0.3302995 + t2 * (0.1801410 + t2 * (-0.0851330 + t2 * 0.0208351))));
  if (ay > ax) {
    angle = HALF_PI - angle;
  }
  if (x < 0) {
    angle = PI - angle;
  }
  if (y < 0) {
    angle = -angle;
  }
  return angle;
}
